import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import FormRow from '../../../../../components/FormRow';
import InputHandler from '../../../../../components/InputHandler';

const optionFormatter =
  (opt: number | string) =>
  (name: number | string): React.JSX.Element => {
    return <FormattedMessage id={`imaging.${opt}.${name}`} />;
  };

const ScintigraphyForm = ({ formData, disabledFields = [] }: IOwnProps): React.JSX.Element => {
  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!disabledFields.includes('date')}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="imaging.imagingType">
        <InputHandler
          type="Radio"
          editing={true}
          name="imagingType"
          formData={formData}
          options={['pet', 'datscan', 'other']}
          optionFormatter={optionFormatter('opts.imagingType')}
          dependentFieldsList={() => ['tracer', 'otherType', 'amyloidTracer', 'result']}
        />
      </FormRow>
      {formData.document.imagingType === 'pet' && (
        <React.Fragment>
          <FormRow title="imaging.tracer">
            <InputHandler
              type="Radio"
              editing={true}
              name="tracer"
              formData={formData}
              options={['fdg', 'amyloid', 'other']}
              optionFormatter={optionFormatter('opts.tracer')}
              dependentFieldsList={() => ['result', 'otherTracer', 'amyloidTracer']}
            />
          </FormRow>
          {formData.document.tracer === 'amyloid' && (
            <FormRow title="imaging.amyloidTracer">
              <InputHandler
                type="Radio"
                editing={true}
                name="amyloidTracer"
                options={['18f-flutemetamol', '111c-pib', 'other']}
                optionFormatter={optionFormatter('opts.amyloidTracer')}
                formData={formData}
                dependentFieldsList={() => ['otherTracer']}
              />
            </FormRow>
          )}
          <FormRow
            title="imaging.whatTracer"
            condition={formData.document.tracer === 'other' || formData.document.amyloidTracer === 'other'}
          >
            <InputHandler type="TextField" editing={true} name="otherTracer" formData={formData} />
          </FormRow>
        </React.Fragment>
      )}
      <FormRow title="imaging.whatOther" condition={formData.document.imagingType === 'other'}>
        <InputHandler type="TextField" editing={true} name="otherType" formData={formData} />
      </FormRow>
      <FormRow
        title="imaging.result"
        condition={
          !!(
            formData.document.imagingType === 'other' ||
            formData.document.imagingType === 'datscan' ||
            (formData.document.imagingType === 'pet' && formData.document.tracer)
          )
        }
      >
        <InputHandler
          type="Radio"
          editing={true}
          name="result"
          formData={formData}
          options={
            formData.document.tracer === 'amyloid'
              ? ['amyloidPosi', 'amyloidNega']
              : ['normal', 'mildlyAbnormal', 'moderatelyAbnormal', 'stronglyAbnormal']
          }
          optionFormatter={optionFormatter('opts.result')}
          dependentFieldsList={() => ['symmetryOfFinding']}
        />
      </FormRow>
      {(formData.document.result === 'mildlyAbnormal' ||
        formData.document.result === 'moderatelyAbnormal' ||
        formData.document.result === 'stronglyAbnormal') && (
        <FormRow title="imaging.symmetryOfFinding">
          <InputHandler
            type="Radio"
            editing={true}
            name="symmetryOfFinding"
            formData={formData}
            options={['symmetric', 'asymmetric']}
            optionFormatter={optionFormatter('opts.symmetricAsymmetric')}
          />
        </FormRow>
      )}
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IScintigraphy>;
  disabledFields?: string[];
}

export default ScintigraphyForm;
